import { makeStyles } from "@material-ui/core/styles";
import Flex from "components/grid/Flex";
import { Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { niagara } from "components/colors/Colors";
import { isAnyPresent, makeHistory } from "../../HistoryUtils";
import _ from "lodash";
import HistoryContainerAndTitle from "./HistoryContainerAndTitle";
import Label from "./Label";
import Value from "./Value";
import moment from "moment";

const useStyles = makeStyles((theme) => ({}));

const BmiRange = (props) => {
    const { rangeText, bmiText, rangeLabel, style } = props;
    const { t } = useTranslation();
    const isActive = bmiText === rangeLabel;
    return (
        <Flex
            item
            container
            column
            center
            style={{
                margin: "0px 10px",
                color: isActive ? "black": "grey",
                borderBottom: `4px solid ${isActive ? "darkGreen" : "lightgreen"}`,
                ...style
            }}
        >
            <Typography style={{ fontWeight: isActive ? "bold": "unset", fontSize: 13 }}>{t(`global.${rangeLabel}`)}</Typography>
            <Typography style={{ fontWeight: isActive ? "bold": "unset", fontSize: 16 }}>{rangeText}</Typography>
            <div style={{ height: 10 }} />
        </Flex>
    );
};

const BmiRanges = (props) => {
    const { height, bmiText } = props;
    const heightInMeter = height / 100;
    const normalLowerRange = ((_.round(18.5 * heightInMeter ** 2, 1) || '-') + " kg").replaceAll(".", ",");
    const normalUpperRange = ((_.round(25 * heightInMeter ** 2, 1) || '-') + " kg").replaceAll(".", ",");
    const obeseLowerRange = ((_.round(30 * heightInMeter ** 2, 1) || '-') + " kg").replaceAll(".", ",");
    return (
        <Flex item container style={{ margin: "0px 15px" }}>
            <BmiRange {...{ rangeText: `< ${normalLowerRange}`, bmiText, rangeLabel: "Underweight", style:{alignItems: 'flex-start'} }} />
            <BmiRange {...{ rangeText: `${normalLowerRange} - ${normalUpperRange}`, bmiText, rangeLabel: "Normal" }} />
            <BmiRange {...{ rangeText: `${normalUpperRange} - ${obeseLowerRange}`, bmiText, rangeLabel: "Overweight" }} />
            <BmiRange {...{ rangeText: `> ${obeseLowerRange}`, bmiText, rangeLabel: "Obese" , style:{alignItems: 'flex-end'}}} />
        </Flex>
    );
};

function getBmiText(bmi) {
    if (bmi < 18.5) {
        return "Underweight";
    } else if (bmi < 24.9) {
        return "Normal";
    } else if (bmi < 29.9) {
        return "Overweight";
    } else {
        return "Obese";
    }
}

/**
 * const {t} = useTranslation();
 *     const lang = useCallback((name) => t(`anamnesis.general.${name}`), [t]);
 * label={t("case.date-of-birth")}
 * label={lang('landOfBirthLabel')}
 * label={t("case.sex")}
 * {t("global.male-bio-sex")}
 * {t(`global.female-bio-sex`)}
 * label={lang('heightLabel')}
 * label={lang('weightLabel')}
 * {lang('hasMenopauseQuestion')}
 * name="hasMenopause"
 * label={lang('houseDoctorLabel')}
 */
const GeneralHistory = ({ anamnesisHistory, patientData }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const history = makeHistory(anamnesisHistory);
    const height = history.getLatestValueOf("height");
    const weight = history.getLatestValueOf("weight");
    const bmi = weight ? weight / (height / 100) ** 2 : 0;
    const bmiText = weight ? getBmiText(bmi): '';
    const age = moment().diff(moment(patientData?.dateOfBirth), 'years');
    const hasMenopause = history.getLatestValueOf("hasMenopause")
        ? t("global.TRUE")
        : t("global.FALSE");

    return (
        <HistoryContainerAndTitle title={t(`anamnesis.general.title`)} isVisible>
            <Flex item container alignItems={"center"}>
                <Flex item grow={0} container column style={{ marginTop: 6 }}>

                    <Flex item container >
                        <Flex item grow={0} container spaceBetween alignItems={"center"} style={{ minWidth: 200 , marginRight: 10}}>
                            <Label>{t("global.age")}:</Label>
                            <Value>{age}</Value>
                        </Flex>
                        <Flex item  container spaceBetween alignItems={"center"} style={{ minWidth: 200 }}>
                            <Label>{t("anamnesis.general.landOfBirthLabel")}:</Label>
                            <Value grey>{history.getLatestValueOf("landOfBirth")}</Value>
                        </Flex>
                    </Flex>

                    <Flex item container >
                        <Flex item grow={0} container spaceBetween alignItems={"center"} style={{ minWidth: 200, marginRight: 10}}>
                            <Label>{t("anamnesis.general.genderLabel")}:</Label>
                            <Value>{t(`global.bio-sex.${patientData?.bioSex}`)}</Value>
                        </Flex>
                        <Flex item grow={0} container spaceBetween alignItems={"center"} style={{ minWidth: 150, marginRight: 10}}>
                            <Label style={{marginRight: 40}}>{t("anamnesis.general.height")}:</Label>
                            <Value>{height}</Value>
                            <span style={{ marginLeft: 4 }} />
                            <Label>cm</Label>
                        </Flex>
                        <Flex item grow={0} container spaceBetween alignItems={"center"} style={{ minWidth: 100}}>
                            <Label>{t("anamnesis.general.weight")}:</Label>
                            <Value>{weight}</Value>
                            <span style={{ marginLeft: 4 }} />
                            <Label>kg</Label>
                        </Flex>
                    </Flex>

                    <Flex item container style={{ maxWidth:  'max(30vw, 530px)' }}>
                        {
                            patientData?.bioSex === 'WOMAN' &&
                            <Flex item grow={0} container spaceBetween alignItems={"center"} style={{ minWidth: 200, marginRight: 10}}>
                                <Label>{t("anamnesis.general.hasMenopause")}:</Label>
                                <Value>{hasMenopause}</Value>
                            </Flex>
                        }

                        <Flex item container spaceBetween alignItems={"center"} style={{ minWidth: 200}}>
                            <Label>{t("anamnesis.general.houseDoctor")}:</Label>
                            <Value grey>{history.getLatestValueOf("houseDoctor")}</Value>
                        </Flex>

                    </Flex>
                </Flex>
                <Flex item />
                <Flex item container column center grow={0} style={{ marginRight: 20 }}>
                    <Typography style={{ fontWeight: "bold", fontSize: 32 }}>{_.round(bmi, 1)}</Typography>
                    <Typography style={{ fontWeight: "bold", fontSize: 22 }}>{bmiText} BMI</Typography>
                </Flex>

                <BmiRanges {...{ bmiText, height }} />
            </Flex>
        </HistoryContainerAndTitle>
    );
};

export default GeneralHistory;
